import { PASSWORD_MIN_LENGTH, passwordSchema } from "@procuremate/shared";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormError } from "@/components/layout/auth-layout";
import { Button } from "@/components/ui/button";
import { TextField } from "@/components/ui/text-field";

/** New password + confirmation, validated with the shared password policy before submitting. */
export function SetPasswordForm({
  submitLabel,
  pending,
  serverError,
  onSubmit,
}: {
  submitLabel: string;
  pending: boolean;
  serverError?: string | undefined;
  onSubmit: (password: string) => void;
}) {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [fieldError, setFieldError] = useState<{ password?: string; confirm?: string }>({});

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!passwordSchema.safeParse(password).success) {
      setFieldError({ password: t("auth.passwordTooShort", { min: PASSWORD_MIN_LENGTH }) });
      return;
    }
    if (password !== confirm) {
      setFieldError({ confirm: t("auth.passwordsDoNotMatch") });
      return;
    }
    setFieldError({});
    onSubmit(password);
  }

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4">
      <TextField
        id="new-password"
        label={t("auth.newPassword")}
        type="password"
        autoComplete="new-password"
        required
        value={password}
        error={fieldError.password}
        onChange={(event) => setPassword(event.target.value)}
      />
      <p className="-mt-2 text-xs text-fg-muted">{t("auth.passwordHint", { min: PASSWORD_MIN_LENGTH })}</p>
      <TextField
        id="confirm-password"
        label={t("auth.confirmPassword")}
        type="password"
        autoComplete="new-password"
        required
        value={confirm}
        error={fieldError.confirm}
        onChange={(event) => setConfirm(event.target.value)}
      />
      <FormError message={serverError} />
      <Button type="submit" className="mt-2 w-full" disabled={pending}>
        {pending ? t("auth.saving") : submitLabel}
      </Button>
    </form>
  );
}
